import React, { useState, useEffect, useRef } from "react";
import FinetuneSettings from "../../../shared/FinetuneSettings";
import { FinetuneDataset } from "../../../../model_gen/finetuning";
import { Card, CardContent, CardHeader, CardTitle } from "../../../ui/card";
import { Button } from "../../../ui/button";
import { 
  SettingsIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  AlertTriangleIcon
} from "lucide-react";
import { notification } from "antd";

interface FinetuneSettingsStepProps {
  exportPrompt: string;
  setExportPrompt: (value: string) => void;
  fourbit: boolean;
  setFourbit: (value: boolean) => void;
  baseModel: string;
  setBaseModel: (value: string) => void;
  baseFinetuneRunId?: number | null;
  setBaseFinetuneRunId?: (value: number | null) => void;
  useBaseFinetunedModel?: boolean;
  setUseBaseFinetunedModel?: (value: boolean) => void;
  baseContextApplicationId?: number;
  baseContextIntentGroupId?: number;
  baseContextIntentId?: number;
  promptDeletions: string[];
  setPromptDeletions: (value: string[]) => void;
  warmupSteps: number;
  setWarmupSteps: (value: number) => void;
  warmupRatio: number;
  setWarmupRatio: (value: number) => void;
  learningRate: number;
  setLearningRate: (value: number) => void;
  perDeviceTrainBatchSize: number;
  setPerDeviceTrainBatchSize: (value: number) => void;
  gradientAccumulationSteps: number;
  setGradientAccumulationSteps: (value: number) => void;
  trainOnResponsesOnly: boolean;
  setTrainOnResponsesOnly: (value: boolean) => void;
  trainSplit: number;
  setTrainSplit: (value: number) => void;
  alpha: number;
  setAlpha: (value: number) => void;
  rank: number;
  setRank: (value: number) => void;
  epochs: number;
  setEpochs: (value: number) => void;
  weightDecay: number;
  setWeightDecay: (value: number) => void;
  dropout: number;
  setDropout: (value: number) => void;
  isExporting: boolean;
  selectedSubDataset?: FinetuneDataset | null;
  setSelectedSubDataset?: (dataset: FinetuneDataset | null) => void;
  datasetId: number;
  rslora: boolean;
  setRslora: (value: boolean) => void;
  totalSamples?: number;
}

const FinetuneSettingsStep: React.FC<FinetuneSettingsStepProps> = ({
  exportPrompt,
  setExportPrompt,
  fourbit,
  setFourbit,
  baseModel,
  setBaseModel,
  baseFinetuneRunId,
  setBaseFinetuneRunId,
  useBaseFinetunedModel,
  setUseBaseFinetunedModel,
  baseContextApplicationId,
  baseContextIntentGroupId,
  baseContextIntentId,
  promptDeletions,
  setPromptDeletions,
  warmupSteps,
  setWarmupSteps,
  warmupRatio,
  setWarmupRatio,
  learningRate,
  setLearningRate,
  perDeviceTrainBatchSize,
  setPerDeviceTrainBatchSize,
  gradientAccumulationSteps,
  setGradientAccumulationSteps,
  trainOnResponsesOnly,
  setTrainOnResponsesOnly,
  trainSplit,
  setTrainSplit,
  alpha,
  setAlpha,
  rank,
  setRank,
  epochs,
  setEpochs,
  weightDecay,
  setWeightDecay,
  dropout,
  setDropout,
  isExporting,
  selectedSubDataset,
  setSelectedSubDataset,
  datasetId,
  rslora,
  setRslora,
  totalSamples,
}) => {
  const [advancedExpanded, setAdvancedExpanded] = useState<boolean>(false);
  const rankValidationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // For intent group finetunes
  const intentGroupId: number | null = null;

  // Validate rank is not greater than alpha with debounce
  useEffect(() => {
    // Clear any existing timeout
    if (rankValidationTimeoutRef.current) {
      clearTimeout(rankValidationTimeoutRef.current);
    }

    // Set a new timeout to check after user stops typing
    rankValidationTimeoutRef.current = setTimeout(() => {
      if (rank > alpha) {
        notification.warning({
          message: "Configuration Warning",
          description: "Rank should not be greater than Alpha for optimal results"
        });
      }
    }, 800); // 800ms debounce delay

    // Cleanup function to clear timeout when component unmounts or dependencies change
    return () => {
      if (rankValidationTimeoutRef.current) {
        clearTimeout(rankValidationTimeoutRef.current);
      }
    };
  }, [rank, alpha]);

  // Make sure we're not overriding explicit zeros with default values
  const getDropoutValue = () => {
    // If dropout is explicitly set (including to 0), use that value
    if (dropout !== undefined && dropout !== null) {
      return dropout;
    }
    // Otherwise use the default
    return 0.05;
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center space-y-3">
        <h2 className="text-2xl font-gc-demi-bold text-foreground">
          Finetune Settings
        </h2>
        <p className="text-muted-foreground font-gc-reg">
          Configure your model fine-tuning parameters
        </p>
      </div>

      {/* Basic Settings Card */}
      <Card className="border-border">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-lg font-gc-med text-foreground">
            <SettingsIcon className="w-5 h-5 text-maitai-green" />
            Basic Settings
          </CardTitle>
        </CardHeader>
        <CardContent>
          <FinetuneSettings
            datasetId={datasetId}
            exportPrompt={exportPrompt}
            setExportPrompt={setExportPrompt}
            fourbit={fourbit}
            setFourbit={setFourbit}
            baseModel={baseModel}
            setBaseModel={setBaseModel}
            baseFinetuneRunId={baseFinetuneRunId}
            setBaseFinetuneRunId={setBaseFinetuneRunId}
            useBaseFinetunedModel={useBaseFinetunedModel}
            setUseBaseFinetunedModel={setUseBaseFinetunedModel}
            baseContextApplicationId={baseContextApplicationId}
            baseContextIntentGroupId={baseContextIntentGroupId}
            baseContextIntentId={baseContextIntentId}
            promptDeletions={promptDeletions}
            setPromptDeletions={setPromptDeletions}
            warmupSteps={warmupSteps}
            setWarmupSteps={setWarmupSteps}
            warmupRatio={warmupRatio}
            setWarmupRatio={setWarmupRatio}
            learningRate={learningRate}
            setLearningRate={setLearningRate}
            perDeviceTrainBatchSize={perDeviceTrainBatchSize}
            setPerDeviceTrainBatchSize={setPerDeviceTrainBatchSize}
            gradientAccumulationSteps={gradientAccumulationSteps}
            setGradientAccumulationSteps={setGradientAccumulationSteps}
            trainOnResponsesOnly={trainOnResponsesOnly}
            setTrainOnResponsesOnly={setTrainOnResponsesOnly}
            trainSplit={trainSplit}
            setTrainSplit={setTrainSplit}
            alpha={alpha}
            setAlpha={setAlpha}
            rank={rank}
            setRank={setRank}
            epochs={epochs}
            setEpochs={setEpochs}
            weightDecay={weightDecay || 0}
            setWeightDecay={setWeightDecay}
            dropout={getDropoutValue()}
            setDropout={setDropout}
            rslora={rslora}
            setRslora={setRslora}
            isExporting={isExporting}
            hideAdvancedSettings={true}
            intentGroupId={intentGroupId}
            selectedSubDataset={selectedSubDataset}
            setSelectedSubDataset={setSelectedSubDataset}
            totalSamples={totalSamples}
          />
        </CardContent>
      </Card>

      {/* Advanced Settings Card */}
      <Card className="border-border">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-lg font-gc-med text-foreground">
              <SettingsIcon className="w-5 h-5 text-maitai-green" />
              Advanced Settings
            </CardTitle>
            <Button
              variant="ghost"
              onClick={() => setAdvancedExpanded(!advancedExpanded)}
              className="gap-2 text-sm"
            >
              {advancedExpanded ? (
                <>
                  <ChevronUpIcon className="w-4 h-4" />
                  Hide Advanced
                </>
              ) : (
                <>
                  <ChevronDownIcon className="w-4 h-4" />
                  Show Advanced
                </>
              )}
            </Button>
          </div>
        </CardHeader>
        {advancedExpanded && (
          <CardContent>
            {rank > alpha && (
              <div className="mb-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                <div className="flex items-center gap-2">
                  <AlertTriangleIcon className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />
                  <p className="text-sm font-gc-med text-yellow-800 dark:text-yellow-200">
                    Configuration Warning
                  </p>
                </div>
                <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                  Rank should not be greater than Alpha for optimal results
                </p>
              </div>
            )}
            
            <FinetuneSettings
              datasetId={datasetId}
              exportPrompt={exportPrompt}
              setExportPrompt={setExportPrompt}
              fourbit={fourbit}
              setFourbit={setFourbit}
              baseModel={baseModel}
              setBaseModel={setBaseModel}
              promptDeletions={promptDeletions}
              setPromptDeletions={setPromptDeletions}
              warmupSteps={warmupSteps}
              setWarmupSteps={setWarmupSteps}
              warmupRatio={warmupRatio}
              setWarmupRatio={setWarmupRatio}
              learningRate={learningRate}
              setLearningRate={setLearningRate}
              perDeviceTrainBatchSize={perDeviceTrainBatchSize}
              setPerDeviceTrainBatchSize={setPerDeviceTrainBatchSize}
              gradientAccumulationSteps={gradientAccumulationSteps}
              setGradientAccumulationSteps={setGradientAccumulationSteps}
              trainOnResponsesOnly={trainOnResponsesOnly}
              setTrainOnResponsesOnly={setTrainOnResponsesOnly}
              trainSplit={trainSplit}
              setTrainSplit={setTrainSplit}
              alpha={alpha}
              setAlpha={setAlpha}
              rank={rank}
              setRank={setRank}
              epochs={epochs}
              setEpochs={setEpochs}
              weightDecay={weightDecay || 0}
              setWeightDecay={setWeightDecay}
              dropout={getDropoutValue()}
              setDropout={setDropout}
              rslora={rslora}
              setRslora={setRslora}
              isExporting={isExporting}
              showOnlyAdvancedSettings={true}
              intentGroupId={intentGroupId}
              selectedSubDataset={selectedSubDataset}
              setSelectedSubDataset={setSelectedSubDataset}
            />
          </CardContent>
        )}
      </Card>
    </div>
  );
};

export default FinetuneSettingsStep;
